import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Row,
  Col,
  Typography,
  Card,
  Button,
  message,
  App as AntdApp,
} from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  syncServiceProviderMaterials,
  fetchAllOperatorAdditiveMaterials,
  mapOperatorAdditiveMaterialsToServiceProviderMaterial,
} from '$api/evoAPIs'

import { StyledLayout } from '$globalStyledComponents/Layout'
import Footer from '$components/Footer'
import AddAdditiveMaterial from './AddAdditiveMaterial'
import MaterialInfo from './MaterialInfo'
import Tooltip from '$components/Tooltip'
import TopKpiCard from './TopKpiCard'
import OperatorAdditiveMaterialsTable from './OperatorAdditiveMaterialsTable'
import ServiceProviderAdditiveMaterialsTable from './ServiceProviderAdditiveMaterialsTable'
import { useUser } from '$context/user'

const { Content } = StyledLayout
const { Title } = Typography

const MaterialMapping = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { notification } = AntdApp.useApp()
  const { isServiceProvider } = useUser()

  const [
    selectedServiceProviderAdditiveMaterial,
    setSelectedServiceProviderAdditiveMaterial,
  ] = useState(null)
  const [highlightedMaterialId, setHighlightedMaterialId] = useState(null)
  const [listActionTrigger, setListActionTrigger] = useState(0)
  const [
    operatorAdditiveMaterialClickedForInfo,
    setOperatorAdditiveMaterialClickedForInfo,
  ] = useState(null)
  const [
    selectedOperatorAdditiveMaterialIds,
    setSelectedOperatorAdditiveMaterialIds,
  ] = useState(new Set())

  const syncMutation = useMutation({
    mutationFn: () =>
      syncServiceProviderMaterials({ providerName: 'MakerVerse' }),
    onSuccess: () => {
      message.success(
        t('sync_successful', 'Material list synced with MakerVerse.'),
      )
      queryClient.invalidateQueries(['serviceProviderMaterials', 'MakerVerse'])
    },
    onError: () => {
      message.error(t('sync_failed', 'Failed to sync with MakerVerse.'))
    },
  })

  const {
    data: operatorAdditiveMaterialsData,
    isLoading: isLoadingOperatorAdditiveMaterials,
  } = useQuery(
    ['operatorAdditiveMaterialsForMapperPage'],
    async () => {
      let allMaterials = []
      let currentPage = 1
      let hasMorePages = true
      let totalCount = 0

      while (hasMorePages) {
        const pageData = await fetchAllOperatorAdditiveMaterials({
          queryKey: [
            'operatorAdditiveMaterialsForMapperPage',
            { page: currentPage, size: 100 },
          ],
        })

        if (pageData.items && pageData.items.length > 0) {
          allMaterials = [...allMaterials, ...pageData.items]
          totalCount = pageData.total

          const totalFetched = currentPage * 100
          hasMorePages = totalFetched < pageData.total
          currentPage += 1
        } else {
          hasMorePages = false
        }
      }

      return {
        items: allMaterials,
        size: allMaterials.length,
        total: totalCount,
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  )

  const handleServiceProviderMaterialSelect = (_materialId, materialObject) => {
    setSelectedServiceProviderAdditiveMaterial(materialObject) // Store the full object
    let existingMappings = new Set()
    if (materialObject?.mapped_operator_additive_material_ids) {
      const mappings = Array.isArray(
        materialObject.mapped_operator_additive_material_ids,
      )
        ? materialObject.mapped_operator_additive_material_ids
        : []
      existingMappings = new Set(mappings.map((id) => id.toString()))
    }
    setSelectedOperatorAdditiveMaterialIds(existingMappings)
    setOperatorAdditiveMaterialClickedForInfo(null)
  }

  const operatorAdditiveMaterialNameMap = useMemo(() => {
    if (!operatorAdditiveMaterialsData || !operatorAdditiveMaterialsData.items)
      return {}
    const nameMap = operatorAdditiveMaterialsData.items.reduce(
      (acc, material) => {
        acc[material.id.toString()] = material.material_name
        return acc
      },
      {},
    )
    return nameMap
  }, [operatorAdditiveMaterialsData])

  const mapMaterialsApiMutation = useMutation(
    mapOperatorAdditiveMaterialsToServiceProviderMaterial,
    {
      onSuccess: (updatedServiceProviderMaterial) => {
        notification.success({ message: t('mapping_successful_message') })

        const newMappedIds = (
          updatedServiceProviderMaterial.mapped_operator_additive_material_ids ||
          []
        ).map((id) => id.toString())

        const newMappedIdsSet = new Set(newMappedIds)
        setSelectedOperatorAdditiveMaterialIds(newMappedIdsSet)

        queryClient.invalidateQueries({ queryKey: [] })
        setListActionTrigger((prev) => prev + 1)
      },
      onError: (_error) => {
        notification.error({ message: t('mapping_failed_message') })
      },
    },
  )

  const handleMapClick = () => {
    if (!selectedServiceProviderAdditiveMaterial) {
      notification.warning({ message: t('select_additive_material_first') })
      return
    }
    mapMaterialsApiMutation.mutate({
      additiveMaterialId: selectedServiceProviderAdditiveMaterial.id,
      operatorAdditiveMaterialIds: selectedOperatorAdditiveMaterialIds,
    })
  }

  const handleUnmapClick = () => {
    if (!selectedServiceProviderAdditiveMaterial) return

    const originalMappedIds = new Set(
      (
        selectedServiceProviderAdditiveMaterial.mapped_operator_additive_material_ids ||
        []
      ).map(String),
    )

    const idsToUnmap = selectedOperatorAdditiveMaterialIds
    idsToUnmap.forEach((id) => originalMappedIds.delete(id))

    mapMaterialsApiMutation.mutate({
      additiveMaterialId: selectedServiceProviderAdditiveMaterial.id,
      operatorAdditiveMaterialIds: originalMappedIds,
    })
  }

  const mapButtonIsDisabled = !selectedServiceProviderAdditiveMaterial

  const mapButtonText =
    selectedOperatorAdditiveMaterialIds.size > 0
      ? `${t('map_material_button')} (${selectedOperatorAdditiveMaterialIds.size})`
      : t('map_material_button')

  const showUnmapButton = useMemo(() => {
    if (
      !selectedServiceProviderAdditiveMaterial ||
      selectedOperatorAdditiveMaterialIds.size === 0
    ) {
      return false
    }
    const originalMappedIds = new Set(
      (
        selectedServiceProviderAdditiveMaterial.mapped_operator_additive_material_ids ||
        []
      ).map(String),
    )
    for (const id of selectedOperatorAdditiveMaterialIds) {
      if (originalMappedIds.has(id)) {
        return true
      }
    }
    return false
  }, [
    selectedServiceProviderAdditiveMaterial,
    selectedOperatorAdditiveMaterialIds,
  ])

  const handleOperatorAdditiveMaterialSelectionChange = (newSelectedIdsSet) => {
    setSelectedOperatorAdditiveMaterialIds(newSelectedIdsSet)
  }

  const handleOperatorAdditiveMaterialRowClickForInfo = (record) => {
    setOperatorAdditiveMaterialClickedForInfo(record)
  }

  return (
    <StyledLayout className="layout">
      <Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '30px 0',
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            {t('material_mapping_title', 'Material Mapping')}
          </Title>
          <div>
            <Button
              onClick={() => syncMutation.mutate()}
              loading={syncMutation.isLoading}
              style={{ marginRight: '16px' }}
            >
              {t('sync_with_makerverse', 'Sync with MakerVerse')}
            </Button>
          </div>
        </div>

        <TopKpiCard t={t} />

        <div className="contentcard" style={{ marginTop: '24px' }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  isServiceProvider ? (
                    <AddAdditiveMaterial
                      setHighlightedMaterialId={setHighlightedMaterialId}
                      setListActionTrigger={setListActionTrigger}
                    />
                  ) : (
                    <>{t('service_provider_additive_materials.header')}</>
                  )
                }
                style={{ height: '100%' }}
              >
                <ServiceProviderAdditiveMaterialsTable
                  onMaterialSelect={handleServiceProviderMaterialSelect}
                  selectedMaterialIdForMapping={
                    selectedServiceProviderAdditiveMaterial?.id
                  }
                  highlightedMaterialId={highlightedMaterialId}
                  onHighlightComplete={() => setHighlightedMaterialId(null)}
                  operatorAdditiveMaterialNameMap={
                    operatorAdditiveMaterialNameMap
                  }
                  selectedOperatorAdditiveMaterialIds={
                    selectedOperatorAdditiveMaterialIds
                  }
                  listActionTrigger={listActionTrigger}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Row gutter={[16, 16]} style={{ height: '100%' }}>
                <Col xs={24} xl={14}>
                  <Card
                    title={t('operator_additive_materials.header')}
                    style={{ height: '100%' }}
                  >
                    <OperatorAdditiveMaterialsTable
                      onSelectionChange={
                        handleOperatorAdditiveMaterialSelectionChange
                      }
                      operatorAdditiveMaterialsData={
                        operatorAdditiveMaterialsData
                      }
                      isLoadingOperatorAdditiveMaterials={
                        isLoadingOperatorAdditiveMaterials
                      }
                      selectedOperatorAdditiveMaterialIds={
                        selectedOperatorAdditiveMaterialIds
                      }
                      onRowClick={handleOperatorAdditiveMaterialRowClickForInfo}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={10}>
                  <Card
                    title={
                      <span style={{ fontSize: '14px', fontWeight: 500 }}>
                        {t('material_info_title')}
                      </span>
                    }
                    style={{
                      height: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    bodyStyle={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      padding: 0,
                    }}
                  >
                    <div
                      style={{ flex: 1, overflowY: 'auto', padding: '24px' }}
                    >
                      <MaterialInfo
                        selectedOperatorAdditiveMaterialForInfo={
                          operatorAdditiveMaterialClickedForInfo
                        }
                        selectedOperatorAdditiveMaterialIds={
                          selectedOperatorAdditiveMaterialIds
                        }
                        operatorAdditiveMaterialsData={
                          operatorAdditiveMaterialsData?.items
                        }
                      />
                    </div>
                    <div
                      style={{
                        padding: '16px 24px',
                        borderTop: '1px solid #f0f0f0',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Button
                        type="primary"
                        onClick={handleMapClick}
                        disabled={mapButtonIsDisabled}
                        style={{ width: '100%' }}
                        size="medium"
                      >
                        {mapButtonText}
                      </Button>
                      {showUnmapButton && (
                        <Tooltip
                          title={t(
                            'unmap_tooltip',
                            t('material_mapper_toolip'),
                          )}
                        >
                          <Button
                            type="primary"
                            danger
                            onClick={handleUnmapClick}
                            disabled={mapButtonIsDisabled}
                            style={{ width: '100%', marginTop: '1vh' }}
                            size="medium"
                          >
                            {t('unmap_button', 'Unmap Selected')}
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Content>
      <Footer />
    </StyledLayout>
  )
}

export default MaterialMapping
